import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useNotification } from '../hooks/useNotification';
import axiosInstance from '../lib/axios';
import { EffectiveInputSchema, InputDryRunResult } from '../types/admin';

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

export const llmModelInputKeys = {
  all: ['llm-model-input'] as const,
  effective: (publicId?: string) =>
    [...llmModelInputKeys.all, 'effective', publicId ?? ''] as const,
};

/** Effective (format + overrides + model-only) input fields of a saved LLM model. */
export const useLLMModelEffectiveInputSchema = (publicId?: string) =>
  useQuery({
    queryKey: llmModelInputKeys.effective(publicId),
    enabled: !!publicId,
    queryFn: async () => {
      const response = await axiosInstance.get<ApiResponse<EffectiveInputSchema>>(
        `/v1/admin/llm-models/${publicId}/effective-input-schema`
      );
      return response.data.data as EffectiveInputSchema;
    },
  });

export const useLLMModelInputDryRun = () => {
  const notification = useNotification();

  return useMutation({
    mutationFn: async ({
      publicId,
      values,
    }: {
      publicId: string;
      values: Record<string, unknown>;
    }) => {
      const response = await axiosInstance.post<ApiResponse<InputDryRunResult>>(
        `/v1/admin/llm-models/${publicId}/input-dry-run`,
        { values }
      );
      return response.data.data as InputDryRunResult;
    },
    onError: (error: AxiosError<ApiResponse>) => {
      notification.error(error.response?.data?.message || 'Dry run failed');
    },
  });
};
